// src/utils/loadCreator.js
//
// Who added a load — shown in grey above the dispatcher on Current Loads and
// next to it in Accounting, so it is always clear who uploaded the dispatch
// sheet, whatever their role.
//
// The name is written onto the load when it is created (createdByName), so it
// stays right even if that person is later renamed or removed. Loads made
// before that existed are looked up by the creator's id among everyone in the
// company — not just dispatchers, because admins upload loads too. The email
// recorded with the load is the last resort, for someone no longer listed.

// Everyone in the company, by id. Filled from the users list the loads and
// accounting pages already fetch (see rememberPeople), so no extra reads.
const peopleById = new Map();

/** "First Last" for a user record, or '' when it carries no name at all. */
export function personName(user) {
  if (!user) return '';
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return String(user.name || full || user.displayName || '').trim();
}

/** Called with the company's users whenever that list arrives or changes. */
export function rememberPeople(users) {
  for (const u of users || []) {
    if (u && u.id) peopleById.set(u.id, u);
  }
}

const looksLikeEmail = (s) => typeof s === 'string' && s.includes('@');

/** A company user's record by id (any status), or null. */
export function personForUserId(userId) {
  return (userId && peopleById.get(userId)) || null;
}

/** A company user's name by id, or '' when unknown or nameless. */
export function nameForUserId(userId) {
  return userId ? personName(peopleById.get(userId)) : '';
}

/** The creator's name to show, or null when the load never recorded one. */
export function loadCreatorName(load) {
  if (!load) return null;
  if (load.createdByName && !looksLikeEmail(load.createdByName)) return load.createdByName;
  const person = load.createdBy ? peopleById.get(load.createdBy) : null;
  const name = personName(person);
  if (name) return name;
  return load.createdByName || load.createdByEmail || null;
}

/** "Uploaded by" for a load that came from a dispatch sheet, else "Added by". */
export function loadCreatorVerb(load) {
  const fromSheet = (Array.isArray(load?.dispatchDocuments) && load.dispatchDocuments.length > 0) ||
    /pdf/i.test(String(load?.sourceType || ''));
  return fromSheet ? 'Uploaded by' : 'Added by';
}
